import { catchHttpError, onStatus } from '../catchHttpError';
import { cloudConnectSubscriptionApiClient } from '../clients/cloudConnectSubscriptionApiClient';
import type {
  BillingIntervalChangeResult,
  CheckoutPromotionPreview,
  CheckoutPromotionPreviewRequest,
  CheckoutSessionRequest,
  CloudConnectAddress,
  CloudConnectPaymentMethodApply,
  ConnectInterval,
  PriceParams,
  Prices,
} from './cloudConnect.types';
import { requireData } from './dataOrNull';

export const getSubscription = () =>
  catchHttpError(
    cloudConnectSubscriptionApiClient
      .GET('/subscription/v1/subscription')
      .then((r) => r.data ?? null),
    onStatus(404, () => null)
  );

export const getPaymentHistory = () =>
  cloudConnectSubscriptionApiClient
    .GET('/subscription/v1/payments')
    .then((r) => r.data ?? []);

export const changeSubscriptionBillingInterval = (
  billingInterval: ConnectInterval
): Promise<BillingIntervalChangeResult> =>
  catchHttpError(
    cloudConnectSubscriptionApiClient
      .POST('/subscription/v1/billing-interval', {
        body: { billingInterval },
      })
      .then((): BillingIntervalChangeResult => ({ status: 'success' })),
    onStatus('PaymentRequired', async (error) => {
      const data = (await error.response.json()) as
        | { paymentUrl?: string }
        | undefined;

      return {
        status: 'requires_payment',
        paymentUrl: data?.paymentUrl,
      } satisfies BillingIntervalChangeResult;
    })
  );

export const updateAddress = (address: CloudConnectAddress) =>
  cloudConnectSubscriptionApiClient
    .PUT('/subscription/v1/address', { body: address })
    .then((r) => r.data ?? null);

export const setupPaymentMethod = () =>
  cloudConnectSubscriptionApiClient
    .POST('/subscription/v1/payment-method-setup')
    .then((r) => r.data ?? null);

export const applyPaymentMethod = (body: CloudConnectPaymentMethodApply) =>
  cloudConnectSubscriptionApiClient
    .POST('/subscription/v1/payment-method-apply', { body })
    .then((r) => r.data ?? null);

export const cancelSubscription = () =>
  cloudConnectSubscriptionApiClient.POST(
    '/subscription/v1/subscription/cancel'
  );

export const reactivateSubscription = () =>
  cloudConnectSubscriptionApiClient.DELETE(
    '/subscription/v1/subscription/cancel'
  );

export const getEnterpriseSubscription = () =>
  catchHttpError(
    cloudConnectSubscriptionApiClient
      .GET('/subscription/v1/enterprise/subscription')
      .then((r) => r.data ?? null),
    onStatus(404, () => null)
  );

export const createBillingPortalSession = () =>
  cloudConnectSubscriptionApiClient
    .POST('/subscription/v1/enterprise/customer_portal')
    .then((r) => r.data ?? null);

// TODO: Implement actual cancellation endpoint when available
export const requestToCancelEnterpriseSubscription = () =>
  cloudConnectSubscriptionApiClient
    .POST('/subscription/v1/enterprise/request-to-cancel')
    .then((r) => r.data ?? null);

export const deleteRequestToCancelEnterpriseSubscription = () =>
  cloudConnectSubscriptionApiClient
    .DELETE('/subscription/v1/enterprise/request-to-cancel')
    .then((r) => r.data ?? null);

export const changeEnterpriseSubscriptionBillingInterval = (vars: {
  interval: ConnectInterval;
  seats?: number;
}) =>
  cloudConnectSubscriptionApiClient
    .POST('/subscription/v1/enterprise/billing-interval', {
      body: {
        billingInterval: vars.interval,
        seats: vars.seats,
      },
    })
    .then((r) => r.data ?? null);

export const getPricePreview = (interval: ConnectInterval) =>
  cloudConnectSubscriptionApiClient
    .GET('/subscription/v1/price-preview', {
      params: { query: { interval } },
    })
    .then((r) => r.data ?? null);

export const createCheckoutSession = (body: CheckoutSessionRequest) =>
  cloudConnectSubscriptionApiClient
    .POST('/subscription/v1/checkout', { body })
    .then(requireData);

export const previewCheckoutPromotion = (
  body: CheckoutPromotionPreviewRequest
): Promise<CheckoutPromotionPreview> =>
  cloudConnectSubscriptionApiClient
    .POST('/subscription/v1/checkout/promotion-preview', { body })
    .then(requireData);

export const getCheckoutConfirmation = () =>
  cloudConnectSubscriptionApiClient
    .GET('/subscription/v1/checkout/confirmation')
    .then(requireData);

export const getCheckoutSession = (params: {
  sessionId: string;
  country: string;
}) =>
  cloudConnectSubscriptionApiClient
    .GET('/subscription/v1/checkout/{sessionId}/country/{country}', {
      params: {
        path: {
          sessionId: params.sessionId,
          country: params.country.toLowerCase(),
        },
      },
    })
    .then(requireData);

export const getPrice = (params: PriceParams): Promise<Prices | null> =>
  cloudConnectSubscriptionApiClient
    .GET('/subscription/v1/prices/{productID}/country/{country}', {
      params: {
        path: {
          country: params.country.toLowerCase(),
          productID: params.productID,
        },
      },
    })
    .then((r) => r.data ?? null);

export const getTrialOffer = () =>
  catchHttpError(
    cloudConnectSubscriptionApiClient
      .GET('/subscription/v1/trial-offer')
      .then((r) => r.data ?? null),
    onStatus(404, () => null)
  );
